import { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import moment from "moment";
import { useMemo } from "react";
import { Button, ItemsTable } from "ui";

import {
  ENROLLMENT_STATUS_COLORS,
  ENROLLMENT_STATUS_LABELS
} from "../../constants";
import {
  EMarketingEnrollmentStatus,
  MarketingFlowEnrollment
} from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./EnrollmentsTable.module.scss";

interface EnrollmentsTableProps {
  enrollments: MarketingFlowEnrollment[];
  isLoading?: boolean;
  onStop: (enrollment: MarketingFlowEnrollment) => void;
}

const EnrollmentsTable = ({
  enrollments,
  isLoading,
  onStop
}: EnrollmentsTableProps) => {
  const columns: Array<ColumnDef<MarketingFlowEnrollment, string>> = useMemo(
    () => [
      {
        id: "contactId",
        accessorKey: "contactId",
        header: t("Contact"),
        size: 30,
        minSize: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.contactId}>
            {enrollments[row.index].contactId}
          </span>
        )
      },
      {
        id: "position",
        accessorKey: "position",
        header: t("Next step"),
        size: 10,
        minSize: 10,
        enableSorting: false,
        cell: ({ row }) => (
          <span>#{enrollments[row.index].position + 1}</span>
        )
      },
      {
        id: "nextRunAt",
        accessorKey: "nextRunAt",
        header: t("Due"),
        size: 18,
        minSize: 18,
        enableSorting: false,
        cell: ({ row }) => {
          const { nextRunAt } = enrollments[row.index];

          return nextRunAt ? (
            <span className={styles.nowrap}>
              {moment(nextRunAt).format("DD/MM/YYYY HH:mm")}
            </span>
          ) : (
            <span className={styles.muted}>—</span>
          );
        }
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("Status"),
        size: 14,
        minSize: 14,
        enableSorting: false,
        cell: ({ row }) => {
          const { status } = enrollments[row.index];

          return (
            <StatusBadge
              label={ENROLLMENT_STATUS_LABELS[status]}
              color={ENROLLMENT_STATUS_COLORS[status]}
            />
          );
        }
      },
      {
        id: "enrolledAt",
        accessorKey: "enrolledAt",
        header: t("Enrolled"),
        size: 18,
        minSize: 18,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.nowrap}>
            {moment(enrollments[row.index].enrolledAt).format(
              "DD/MM/YYYY HH:mm"
            )}
          </span>
        )
      },
      {
        id: "actions",
        accessorKey: "id",
        header: "",
        size: 10,
        minSize: 10,
        enableSorting: false,
        cell: ({ row }) => {
          const enrollment = enrollments[row.index];

          return enrollment.status === EMarketingEnrollmentStatus.Active ? (
            <Button
              variant="text-delete"
              size="sm"
              onClick={() => onStop(enrollment)}
            >
              {t("Stop")}
            </Button>
          ) : null;
        }
      }
    ],
    [enrollments]
  );

  return (
    <ItemsTable
      columns={columns}
      data={enrollments}
      isLoading={isLoading}
      skeletonHeight={64}
      emptyListMessage={t("No enrollments yet")}
      rowsClassName={styles.tallRow}
    />
  );
};

export default EnrollmentsTable;
